import { useState, useEffect } from 'react';
import axios from 'axios';
import { XCircle, Trophy } from '@phosphor-icons/react';
import DCEComparatifDialog from './DCEComparatifDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUT_CFG = {
  EN_PREPARATION: { label: 'En préparation', cls: 'bg-[#F5F5F3] text-[#6B6B6B]' },
  EN_COURS: { label: 'En cours', cls: 'bg-[#E3F2FD] text-[#1E3A5F]' },
  CLOTUREE: { label: 'Clôturée', cls: 'bg-[#FFF8E1] text-[#B8860B]' },
  CLOTURE: { label: 'Clôturée', cls: 'bg-[#FFF8E1] text-[#B8860B]' },
  ATTRIBUEE: { label: 'Attribuée', cls: 'bg-[#E8F5E9] text-[#2D6A4F]' },
  ANNULE: { label: 'Annulé', cls: 'bg-[#FEF2F2] text-[#C53030]' },
};


export default function DceHistoriqueModal({ chantierId, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // CSL-07 — DCE dont on consulte le comparatif (lecture seule si terminé)
  const [consulte, setConsulte] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/chantiers/${chantierId}/dce/historique`,
          { withCredentials: true },
        );
        if (!cancelled) setItems(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [chantierId]);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="dce-historique-modal"
    >
      <div className="bg-white max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-[#E0E0DB] flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Historique des consultations DCE
          </h2>
          <button
            onClick={onClose}
            className="text-[#9A9A9A] hover:text-[#2C2C2C]"
            data-testid="btn-close-historique-modal"
          >
            <XCircle size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading && <p className="text-sm text-[#6B6B6B]">Chargement…</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-[#6B6B6B]">Aucun DCE dans l'historique.</p>
          )}
          {!loading && items.length > 0 && (
            <table className="w-full text-sm" data-testid="historique-table">
              <thead>
                <tr className="text-left border-b border-[#E0E0DB] text-xs uppercase tracking-wider text-[#6B6B6B]">
                  <th className="py-2 pr-3">Statut</th>
                  <th className="py-2 pr-3">Créé le</th>
                  <th className="py-2 pr-3">Lots</th>
                  <th className="py-2 pr-3">Invitations</th>
                  <th className="py-2 pr-3">Attribués</th>
                  <th className="py-2 pr-3">Détails</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(d => {
                  const cfg = STATUT_CFG[d.statut] || STATUT_CFG.EN_PREPARATION;
                  return (
                    <tr
                      key={d.id}
                      className="border-b border-[#F5F5F3]"
                      data-testid={`historique-row-${d.id}`}
                    >
                      <td className="py-2 pr-3">
                        <span className={`text-xs px-2 py-0.5 font-bold ${cfg.cls}`}>{cfg.label}</span>
                      </td>
                      <td className="py-2 pr-3 text-[#2C2C2C]">
                        {d.cree_le ? new Date(d.cree_le).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="py-2 pr-3 text-[#2C2C2C]">{d.snapshot_lots_count}</td>
                      <td className="py-2 pr-3 text-[#2C2C2C]">{d.invitations_count}</td>
                      <td className="py-2 pr-3 text-[#2C2C2C]">{d.lots_attribues_count}</td>
                      <td className="py-2 text-[#6B6B6B] text-xs">
                        {d.statut === 'ANNULE' && d.annule_motif && (
                          <span className="italic">« {d.annule_motif.slice(0, 80)}{d.annule_motif.length > 80 ? '…' : ''} »</span>
                        )}
                        {d.statut === 'ANNULE' && d.annule_at && (
                          <div>annulé le {new Date(d.annule_at).toLocaleDateString('fr-FR')}</div>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {d.invitations_count > 0 && (
                          <button
                            onClick={() => setConsulte(d)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A5F] border border-[#1E3A5F] px-2 py-1 hover:bg-[#E3F2FD]"
                            data-testid={`historique-consulter-${d.id}`}
                            title="Consulter le comparatif des offres, la notation et l'export PDF"
                          >
                            <Trophy size={12} /> Consulter
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <DCEComparatifDialog
        chantierId={chantierId}
        dceId={consulte?.id}
        dceStatut={consulte?.statut}
        open={!!consulte}
        onOpenChange={(o) => { if (!o) setConsulte(null); }}
      />
    </div>
  );
}
